import React from 'react'
import { createPortal } from 'react-dom'
import { AnimatedWistorLogo } from './AnimatedWistorLogo'
import { mergeClasses } from '../helpers/styling'
import { ButtonLink } from './basic/Button'
import styles from './login/LoginPageLayout.module.css'
import logo from '../images/wistor-mark-color.svg'
import { PlatformConfigationTopBar } from './ConfigurationTopBar'
import { useWistorAuth } from '../api/auth'
import { useFeatureFlags } from '../flags'
import { Icon } from './widgets/Icon'

export function SingleLoginUnauthorizedView(props: {
  error: 'FORBIDDEN'
  projectPath: string
}) {
  const auth = useWistorAuth()

  const message = auth.status === 'authorized'
    ? `${auth.user.email} is not allowed to access this application.`
    : "Something went unexpectedly wrong. Contact support if this keeps occuring."

  return (
    <div className="size-full">
      {!window.CONNECTED_APPS_STATIC && (
        <PlatformConfigationTopBar projectPath={props.projectPath} />
      )}
      <div className="size-full flex flex-col justify-around items-center bg-[#eaeaea]">
        <div className={"py-8 px-10 rounded-md bg-red-200 text-red-950 flex gap-4 items-center"}>
          <Icon icon="triangle-exclamation" className="text-4xl" />
          <div className="font-medium w-full">{message}</div>
        </div>
      </div>
    </div>
   )
}

export function SingleLoginButton(props: {
  returnTo?: string
}) {
  const returnTo = encodeURI(`${window.location.protocol}//${window.location.host}${props.returnTo ?? ''}`)

  return (
    <SingleUserButton href={`/rest/oidc/login?returnTo=${returnTo}`}>
      Login via login.wistor.nl
    </SingleUserButton>
  )
}

export function SingleLogoutButton() {
  return (
    <SingleUserButton href="/rest/oidc/logout">
      Logout of login.wistor.nl
    </SingleUserButton>
  )
}

export function SingleLoginView(props: {
  returnTo?: string
}) {
  const flags = useFeatureFlags()
  const auth = useWistorAuth()

  const logoutViewPart = auth.status === 'unauthorized' && (
    <div className="mt-4 py-3 px-4 rounded-md bg-red-200 text-red-950">
      <div className="flex gap-4 items-center">
        <Icon icon="triangle-exclamation" />
        <div className="flex flex-col">
          <div className="font-medium w-full">{auth.errorMessage}</div>
        </div>
      </div>
      {auth.status === 'unauthorized' && auth.visibleSingleLogout && (
        <SingleLogoutButton />
      )}
    </div>
  )

  const loginViewPart = flags.singleLogin && (<>
    {((auth.status !== 'unauthorized') || (auth.status === 'unauthorized' && auth.visibleSingleLogin)) && (
      <SingleLoginButton returnTo={props.returnTo ?? ''} />
    )}
    <hr className="h-0 border-t border-l-indigo-900 mt-4" />
  </>)

  return (
    <>
      {logoutViewPart}
      {loginViewPart}
    </>
  )
}

function SingleUserButton(props: { href: string, children: React.ReactNode }) {
  const [visibleOverlay, setVisibleOverlay] = React.useState(false)

  return (
    <>
      <ButtonLink
        variant="outline"
        className={mergeClasses(styles['login-button-sso'], "w-full mt-4")}
        href={props.href}
        onClick={() => setVisibleOverlay(true)}
      >
        <img src={logo} alt="wistor logo" />
        <span>{props.children}</span>
      </ButtonLink>
      {visibleOverlay && createPortal((
        <div className="fixed inset-0 bg-opacity-55 backdrop-blur-md bg-slate-600 flex flex-col justify-around items-center rounded-xl">
          <AnimatedWistorLogo />
        </div>
      ), document.body)}
    </>
  )
}
